import { Box, Flex, HStack, Text, VStack } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import type { TEvaluationComponentProps } from "@src/components/modules/projects/detail/Evaluation/types";
import { getRealPercent } from "@src/components/modules/projects/detail/Evaluation/utils";
import { CardHeader } from "@src/components/modules/projects/detail/Stats/CardHeader";
import { Card } from "@src/components/ui-kit";
import {
  currency,
  currency as currencyFormatter,
  percent,
  percent as percentFormatter,
} from "@src/utils/formatters";
import type { FC } from "react";

type TBarProps = {
  currency: number;
  percent: number;
};

const HUNDRED_PERCENT = 10000;

const EfficiencyBar: FC<TBarProps> = ({ currency, percent }) => {
  const secondBoxColor = getRealPercent(percent) < 100 ? "red.500" : "grey.100";
  const differenceFromHundred = HUNDRED_PERCENT - percent;
  const secondBoxWidth = Math.abs(getRealPercent(differenceFromHundred));

  return (
    <Flex
      pos="relative"
      overflow="hidden"
      w="full"
      h="6"
      bg={secondBoxColor}
      role="group"
      rounded="3xl"
    >
      <Box
        pos="absolute"
        right={`${secondBoxWidth >= 100 ? 100 : secondBoxWidth}%`}
        left="0"
        h="6"
        bg="teal.500"
      />
      <Flex pos="absolute" right="0" left="0" justify="center" h="6">
        <Text
          justifySelf="center"
          alignSelf="center"
          pr="1"
          color="transparent"
          textAlign="center"
          _groupHover={{
            color: "black",
          }}
          transition="color 150ms ease-in"
        >
          {currencyFormatter.formatByWorkspace(currency) +
            " (" +
            percentFormatter.format(
              percent === 0
                ? 0
                : percent < HUNDRED_PERCENT
                  ? HUNDRED_PERCENT + differenceFromHundred
                  : HUNDRED_PERCENT - differenceFromHundred,
            ) +
            ")"}
        </Text>
      </Flex>
    </Flex>
  );
};

export const EfficiencyCard: FC<TEvaluationComponentProps> = ({ project }) => {
  const { remaining_budget, efficiency_percent, total_budget } =
    project.stats.ourWork;

  const textColor =
    getRealPercent(efficiency_percent) < 100 ? "red.500" : undefined;

  return (
    <Card p="4">
      <HStack alignItems="flex-start" justifyContent="space-between" mb="6">
        <CardHeader title={t`Work efficiency`} />
        <VStack alignItems="flex-end">
          <Text
            color={textColor}
            fontSize="2xl"
            fontWeight="bold"
            lineHeight="24px"
          >
            {percent.format(efficiency_percent)}
          </Text>
          <Text color={textColor}>
            ({currency.formatByWorkspace(remaining_budget)})
          </Text>
        </VStack>
      </HStack>
      <EfficiencyBar currency={total_budget} percent={efficiency_percent} />
    </Card>
  );
};
